import { create } from 'zustand';

import { seedNewUser } from '@/entities/profile/model/defaults';
import { describeError, record } from '@/shared/lib/diagnostics';
import type { User } from '@/shared/db/schema';
import { setCurrentUserId } from '@/shared/db/session-context';

import {
  AuthError,
  type Credentials,
  getAuthProvider,
  isRejectedSession,
  type RegisterInput,
} from '../api/auth-provider';

const TOKEN_STORAGE_KEY = 'kairo.token';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  user: User | null;
  error: string | null;

  /** Восстанавливает сессию при запуске. Вызывается один раз в провайдере. */
  bootstrap: () => Promise<void>;
  login: (credentials: Credentials) => Promise<boolean>;
  register: (input: RegisterInput) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
}

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    // Приватный режим или отключённое хранилище: работаем без «запомнить меня».
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* нет доступа к localStorage — сессия проживёт до перезагрузки */
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'idle',
  user: null,
  error: null,

  async bootstrap() {
    const token = readToken();
    if (!token) {
      set({ status: 'anonymous' });
      return;
    }

    set({ status: 'loading' });
    try {
      const session = await getAuthProvider().restore(token);
      if (session) {
        // Слой данных должен узнать владельца раньше, чем отрисуются экраны.
        setCurrentUserId(session.user.id);
        set({ status: 'authenticated', user: session.user });
        return;
      }

      // Сервер ответил и сказал, что сессии нет: токен больше не годится.
      writeToken(null);
      set({ status: 'anonymous' });
    } catch (error) {
      /**
       * Сбой связи — не повод выходить из аккаунта.
       *
       * Раньше любая ошибка стирала токен, и запуск без сети означал
       * принудительный выход из офлайн-приложения: данные на устройстве есть,
       * а показать их некому. Токен снимается только тогда, когда сервер
       * прямо сказал, что он недействителен.
       */
      if (isRejectedSession(error)) {
        writeToken(null);
        set({ status: 'anonymous' });
        return;
      }

      const local = await getAuthProvider().restoreOffline?.(token);
      if (local) {
        setCurrentUserId(local.user.id);
        set({ status: 'authenticated', user: local.user });
        return;
      }

      set({ status: 'anonymous', error: 'Нет связи с сервером. Данные на устройстве не тронуты' });
    }
  },

  async login(credentials) {
    set({ status: 'loading', error: null });
    try {
      const session = await getAuthProvider().login(credentials);
      writeToken(session.token);
      setCurrentUserId(session.user.id);
      set({ status: 'authenticated', user: session.user });
      return true;
    } catch (error) {
      record('error', 'auth.login', error);

      set({
        status: 'anonymous',
        error:
          error instanceof AuthError
            ? error.message
            : `Не удалось войти: ${describeError(error)}`,
      });
      return false;
    }
  },

  async register(input) {
    set({ status: 'loading', error: null });
    try {
      const session = await getAuthProvider().register(input);
      writeToken(session.token);
      setCurrentUserId(session.user.id);
      // Профиль и режим дня создаются до показа интерфейса, иначе первый
      // рендер календаря пришёлся бы на пустое расписание.
      await seedNewUser(session.user.name);
      set({ status: 'authenticated', user: session.user });
      return true;
    } catch (error) {
      /*
        Причина сбоя уходит в журнал диагностики и в само сообщение.

        «Не удалось создать аккаунт» — это тупик: человек нажимает ещё раз,
        получает то же самое и остаётся без приложения, а разбор начинается с
        уточняющих вопросов. Сообщение системы («нет места», «хранилище
        недоступно») он хотя бы может пересказать — или прочитать сам в
        диагностике.
      */
      record('error', 'auth.register', error);

      set({
        status: 'anonymous',
        error:
          error instanceof AuthError
            ? error.message
            : `Не удалось создать аккаунт: ${describeError(error)}`,
      });
      return false;
    }
  },

  async logout() {
    await getAuthProvider().logout();
    writeToken(null);
    setCurrentUserId(null);
    set({ status: 'anonymous', user: null, error: null });
  },

  clearError: () => set({ error: null }),
}));
